/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useMemo, useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import BaseModal from '../ts-components/BaseModal'
import MetricCard, { MetricCardData } from './MetricCard'
import ClicksIcon from '../icons/ClicksIcon'
import ImpressionsIcon from '../icons/ImpressionsIcon'
import {
  BellOutlined,
  MailOutlined,
  HeartFilled,
  PhoneOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import { fetchPostAnalytics, PostAnalyticsData } from './utils'
import CommentIcon from './CommentIcon'

interface PostInsightsModalProps {
  isOpen: boolean
  onClose: () => void
  postId: string
  sendSocialFeed?: boolean
  sendEmail?: boolean
  sendPushNotification?: boolean
  title?: string
  likesCount?: number
  commentsCount?: number
}

const PostInsightsModal: React.FC<PostInsightsModalProps> = ({
  isOpen,
  onClose,
  postId,
  sendSocialFeed = true,
  sendEmail = false,
  sendPushNotification = false,
  title = '',
  likesCount = 0,
  commentsCount = 0,
}) => {
  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState<PostAnalyticsData | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  // Fetch analytics data when modal opens
  useEffect(() => {
    if (isOpen && postId) {
      const loadAnalytics = async () => {
        setIsLoading(true)
        const data = await fetchPostAnalytics(postId)
        setAnalyticsData(data)
        setIsLoading(false)
      }
      loadAnalytics()
    }
  }, [isOpen, postId])

  // Determine which sections to show based on props
  const sectionsToShow = useMemo(() => {
    return {
      socialFeed: sendSocialFeed,
      email: sendEmail,
      pushNotification: sendPushNotification,
      // Always show SMS if there's data
      sms: true,
    }
  }, [sendSocialFeed, sendEmail, sendPushNotification])

  // Build sections data based on analytics data
  const sectionsData = useMemo(() => {
    if (!analyticsData) {
      return {}
    }

    console.log('🔄 PostInsightsModal: Building metrics sections')

    const sections: Record<
      string,
      { title: string; metrics: MetricCardData[]; note?: string }
    > = {}

    // Social Feed Section
    if (sectionsToShow.socialFeed) {
      const socialImpressions = analyticsData.social_feed_impressions || 0
      const socialClicks = analyticsData.social_feed_clicks || 0
      const socialCtr =
        socialImpressions > 0 ? (socialClicks / socialImpressions) * 100 : 0
      sections.socialFeed = {
        title: 'Social Feed',
        metrics: [
          {
            title: 'Impressions',
            value: socialImpressions,
            icon: <ImpressionsIcon size={20} color={colors.PRIMARY} />,
          },
          {
            title: 'Clicks',
            value: socialClicks,
            icon: <ClicksIcon size={20} color={colors.PRIMARY} />,
          },
          {
            title: 'CTR (%)',
            value: Number(socialCtr.toFixed(2)),
            icon: (
              <LineChartOutlined
                style={{ fontSize: 20, color: colors.PRIMARY }}
              />
            ),
          },
          {
            title: 'Likes',
            value: likesCount,
            icon: (
              <HeartFilled style={{ fontSize: 20, color: colors.PRIMARY }} />
            ),
          },
          {
            title: 'Comments',
            value: commentsCount,
            icon: <CommentIcon />,
          },
        ],
      }
    }

    // Email Section
    if (sectionsToShow.email) {
      sections.email = {
        title: 'Emails',
        /*
         * SN-7901 — a Communication can now email people on the roster who hold no
         * account. "Emails Sent" counts them; clicks cannot. Click attribution is written
         * against `campaign_audience`, which is keyed on `user_id`, and the click endpoint
         * itself requires an authenticated session — an un-joined recipient following the
         * link meets a login wall. The two numbers therefore measure different populations
         * on purpose, and saying so beats leaving a ratio that quietly under-reads.
         */
        note: 'Clicks are tracked for recipients with a SportNinja account.',
        metrics: [
          {
            title: 'Emails Sent',
            value: analyticsData.notification_email_sent || 0,
            icon: (
              <MailOutlined style={{ fontSize: 20, color: colors.PRIMARY }} />
            ),
          },
          {
            title: 'Email Clicks',
            value: analyticsData.email_clicks || 0,
            icon: <ClicksIcon size={20} color={colors.PRIMARY} />,
          },
        ],
      }
    }

    // Push Notifications Section
    if (sectionsToShow.pushNotification) {
      sections.pushNotification = {
        title: 'Push Notifications',
        metrics: [
          {
            title: 'Push Sent',
            value: analyticsData.notification_push_sent || 0,
            icon: (
              <BellOutlined style={{ fontSize: 20, color: colors.PRIMARY }} />
            ),
          },
          {
            title: 'Push Clicks',
            value: analyticsData.push_notification_clicks || 0,
            icon: <ClicksIcon size={20} color={colors.PRIMARY} />,
          },
        ],
      }
    }

    // SMS Section (show if there's data)
    if (sectionsToShow.sms && analyticsData.notification_sms_sent > 0) {
      sections.sms = {
        title: 'SMS',
        metrics: [
          {
            title: 'SMS Sent',
            value: analyticsData.notification_sms_sent || 0,
            icon: (
              <PhoneOutlined style={{ fontSize: 20, color: colors.PRIMARY }} />
            ),
          },
        ],
      }
    }

    return sections
  }, [analyticsData, sectionsToShow, likesCount, commentsCount])

  // Create a separator component matching GeneralSettings style
  const sectionSeparatorStyles = css`
    width: 100%;
    height: 1px;
    background: ${colors.NEUTRAL_100};
    border-bottom: 1px solid ${colors.NEUTRAL_100};
    margin: 16px 0;
  `

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Insights for ${title}`}
      desktopWidth='min(50vw, 600px)'
      cancelText='Close'
    >
      {/* Analytics Sections */}
      {isLoading ? (
        // Loading state
        <div
          css={css`
            text-align: center;
            padding: 24px;
          `}
        >
          <Text
            variant='paragraphMedium'
            weight='medium'
            color={colors.NEUTRAL_50}
          >
            Loading analytics...
          </Text>
        </div>
      ) : Object.keys(sectionsData).length > 0 ? (
        // Display sections
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0;
          `}
        >
          {Object.entries(sectionsData).map(([sectionKey, section], index) => (
            <React.Fragment key={sectionKey}>
              {/* Section Title */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  align-self: stretch;
                  margin-bottom: 8px;
                `}
              >
                <Text variant='captionLarge' weight='semiBold'>
                  {section.title}
                </Text>
              </div>

              {section.note && (
                <div
                  css={css`
                    margin-bottom: 8px;
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_100}
                  >
                    {section.note}
                  </Text>
                </div>
              )}

              {/* Section Metrics Grid */}
              <div
                css={css`
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 16px;
                  @media (max-width: 768px) {
                    grid-template-columns: 1fr;
                  }
                `}
              >
                {section.metrics.map((metric) => {
                  const isCtr = metric.title === 'CTR (%)'
                  const card = <MetricCard {...metric} />
                  return isCtr ? (
                    <Tooltip
                      key={`metric-${metric.title}`}
                      title='Click Through Rate (CTR) = Clicks / Impressions'
                      placement='top'
                      zIndex={11000}
                    >
                      {card}
                    </Tooltip>
                  ) : (
                    <React.Fragment key={`metric-${metric.title}`}>
                      {card}
                    </React.Fragment>
                  )
                })}
              </div>

              {/* Separator (except for last section) */}
              {index < Object.keys(sectionsData).length - 1 && (
                <div css={sectionSeparatorStyles} />
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        // No data state
        <div
          css={css`
            text-align: center;
            padding: 16px;
          `}
        >
          <Text
            variant='paragraphMedium'
            weight='medium'
            color={colors.NEUTRAL_50}
          >
            No analytics data available
          </Text>
        </div>
      )}
    </BaseModal>
  )
}

export default PostInsightsModal
